// ComposicaoStatusBanner — D-210 ADM-02 + ADM-03 (web-admin side).
//
// Sibling de apps/web-comite/src/components/ComiteNonOperationalBanner.tsx
// mas com 2 estados visuais:
//   1. Vermelho (alert-error): !operacional (faltam RH/Jurídico/SESMT)
//      — backend bloqueia triagem (Plan 02.2-03 Task 1, 503 missing[])
//   2. Amarelo (alert-warning): operacional MAS requiresExternalMemberPending
//      > 0 — alerta admin que há casos esperando inclusão de membro externo
//   3. null: operacional E sem caso pendente externo (sem ruído visual)
//
// Polling 30s — RESEARCH §Notification Fan-out (~120 req/h por sessão admin
// é trivial para 1-2 admins; SSE/WS é v2). Cleanup interval on unmount;
// cancelled flag previne setState after unmount.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api-client';

interface ComposicaoStatus {
  readonly operacional: boolean;
  readonly missing: ReadonlyArray<string>;
  readonly requiresExternalMemberPending: number;
}

const POLL_INTERVAL_MS = 30_000;

export function ComposicaoStatusBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ComposicaoStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async (): Promise<void> => {
      try {
        const resp = await apiFetch('/api/admin/composicao');
        if (!resp.ok) return;
        const body = (await resp.json()) as ComposicaoStatus;
        if (!cancelled) setStatus(body);
      } catch {
        // Network error — banner stays silent (não bloqueia outros widgets do Dashboard)
      }
    };
    void fetchStatus();
    const interval = setInterval(() => void fetchStatus(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (!status) return null;

  if (!status.operacional) {
    return (
      <div
        className="alert alert-error"
        role="alert"
        data-testid="composicao-banner-non-operational"
      >
        <strong>{t('members.composicao.title_non_operational')}</strong>
        <p>
          {t('members.composicao.detail_non_operational', {
            missing: status.missing.join(', '),
          })}
        </p>
      </div>
    );
  }

  if (status.requiresExternalMemberPending > 0) {
    return (
      <div
        className="alert alert-warning"
        role="status"
        data-testid="composicao-banner-externo-pendente"
      >
        <strong>{t('members.composicao.title_externo_pendente')}</strong>
        <p>
          {t('members.composicao.detail_externo_pendente', {
            count: status.requiresExternalMemberPending,
          })}
        </p>
      </div>
    );
  }

  return null;
}
